// reports/useReportExport.ts
//
// Export a report straight from the screen the user is already on.
//
// The result screens used to send the user to a /report route that rendered a
// preview with its own Download button — a page load and a second click before
// anything resembling a PDF appeared. The preview route still exists for direct
// links, but it is no longer the way you get a file: this hook does the whole
// job from the button that was tapped.
//
// Everything it needs already happens off-screen — the template is cached, the
// sheet is laid out in a detached iframe — so there is nothing to navigate to.

import React, { useCallback, useState } from 'react';
import Toast from '../components/ui/Toast';
import { exportReportPdf, renderReportHtml } from './renderReportPdf';
import { loadReport } from './reportLoaders';
import type { ReportKind } from './templateClient';

export interface ReportExportState {
  /** True while a sheet is being built; drives the button's busy affordance. */
  exporting: boolean;
  /** Set when the report could not be produced at all. */
  error: string | null;
  /** Render this anywhere in the component; Toast portals to the body, so its
   *  position in the tree does not affect layout. */
  errorToast: React.JSX.Element;
  /** Build the report for `id` and hand it to the browser. */
  exportReport: (kind: ReportKind, id: string) => Promise<void>;
  clearError: () => void;
}

const MISSING = 'This report is not ready yet.';
const FAILED = 'Could not build this report.';

export function useReportExport(): ReportExportState {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportReport = useCallback(async (kind: ReportKind, id: string) => {
    // A second tap while the first export is still laying out would race two
    // detached frames onto the same print pipeline.
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const report = await loadReport(kind, id);
      if (!report) {
        setError(MISSING);
        return;
      }
      const html = await renderReportHtml(kind, report.viewModel);
      await exportReportPdf(html, { title: report.title });
    } catch (error_: unknown) {
      setError(error_ instanceof Error ? error_.message : FAILED);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const clearError = useCallback(() => { setError(null); }, []);

  const errorToast = (
    <Toast
      open={error !== null}
      onOpenChange={open => { if (!open) setError(null); }}
      variant="error"
      title="Report unavailable"
      description={error ?? undefined}
      autoCloseMs={5000}
    />
  );

  return { exporting, error, errorToast, exportReport, clearError };
}
